/**
 * API client for Students endpoints
 */

import apiClient from "./client";
import type {
  Student,
  StudentDetail,
  StudentListResponse,
  StudentEnrollmentsResponse,
  StudentInvoicesResponse,
  StudentDiscountsResponse,
  CreateStudentRequest,
  UpdateStudentRequest,
  ListStudentsParams,
  MessageResponse,
} from "@/types/student";

/**
 * Students API endpoints
 */
export const studentsApi = {
  /**
   * List students with pagination and filters
   */
  list: (params?: ListStudentsParams) =>
    apiClient.get<StudentListResponse>("/api/students/v1/", { params }),

  /**
   * Get student by ID with details (includes family and enrollments)
   */
  get: (id: string) => apiClient.get<StudentDetail>(`/api/students/v1/${id}`),

  /**
   * Create new student
   */
  create: (data: CreateStudentRequest) =>
    apiClient.post<Student>("/api/students/v1/", data),

  /**
   * Update student
   */
  update: (id: string, data: UpdateStudentRequest) =>
    apiClient.patch<Student>(`/api/students/v1/${id}`, data),

  /**
   * Withdraw student (soft delete)
   */
  delete: (id: string) =>
    apiClient.delete<MessageResponse>(`/api/students/v1/${id}`),

  /**
   * Get student enrollments
   */
  getEnrollments: (studentId: string) =>
    apiClient.get<StudentEnrollmentsResponse>(
      `/api/students/v1/${studentId}/enrollments`,
    ),

  /**
   * Get student invoices
   */
  getInvoices: (studentId: string) =>
    apiClient.get<StudentInvoicesResponse>(
      `/api/students/v1/${studentId}/invoices`,
    ),

  /**
   * Get student discounts
   */
  getDiscounts: (studentId: string) =>
    apiClient.get<StudentDiscountsResponse>(
      `/api/students/v1/${studentId}/discounts`,
    ),

  /**
   * Export all students (fetches all records for CSV export)
   */
  exportAll: async (params?: Omit<ListStudentsParams, 'page' | 'limit'>) => {
    const allStudents: Student[] = [];
    let page = 1;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const response = await apiClient.get<StudentListResponse>("/api/students/v1/", {
        params: { ...params, page, limit },
      });
      const data = response.data;
      allStudents.push(...data.students);

      hasMore = page < data.meta.total_pages;
      page++;
    }

    return { students: allStudents, meta: { total: allStudents.length, page: 1, limit: allStudents.length, total_pages: 1 } };
  },
};

export default studentsApi;
